import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Shield, Trophy, Users } from "lucide-react";

const TITLE_MAP = {
  "title_kral": { name: "Kral", icon: "👑" },
  "title_maestro": { name: "Maestro", icon: "🎭" },
  "title_kaptan": { name: "Kaptan", icon: "©️" },
  "title_dribbbler": { name: "Dribbbler", icon: "⚽" },
  "title_macin_adami": { name: "Maçın Adamı", icon: "🌟" },
  "title_kaleci_kral": { name: "Kaleci Kral", icon: "🧤" },
  "title_asist_kral": { name: "Asist Kralı", icon: "🎯" },
  "title_gol_makinesi": { name: "Gol Makinesi", icon: "⚽" },
  "title_demir_adam": { name: "Demir Adam", icon: "🦾" },
  "title_hizli_simsek": { name: "Hızlı Şimşek", icon: "⚡" },
  "title_duvar": { name: "Duvar", icon: "🧱" },
  "title_sniper": { name: "Sniper", icon: "🎯" },
  "title_playmaker": { name: "Playmaker", icon: "🧠" },
  "title_tank": { name: "Tank", icon: "🛡️" },
  "title_efsane": { name: "Efsane Oyuncu", icon: "🏆" },
  "title_mvp": { name: "MVP", icon: "⭐" },
  "title_sampiyon": { name: "Şampiyon", icon: "🏅" },
};

const PlayerCard = ({ user }) => {
  const themeColor = user?.themeColor || "#10b981";
  const activeTitle = user?.activeTitle ? TITLE_MAP[user.activeTitle] : null;
  
  return (
    <div className="w-full max-w-sm mx-auto">
      <Card className="overflow-hidden" style={{ borderColor: themeColor }}>
        {/* Banner */}
        <div 
          className="h-32 relative"
          style={{
            background: user?.bannerImage 
              ? `url(${user.bannerImage}) center/cover` 
              : (user?.bannerColor || `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`)
          }}
        >
          {/* Active Title Badge */}
          {activeTitle && (
            <div className="absolute top-2 right-2">
              <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-2 py-1 shadow-lg">
                <span className="mr-1">{activeTitle.icon}</span>
                {activeTitle.name}
              </Badge>
            </div>
          )}
          
          {/* Avatar */}
          <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2">
            <Avatar className="h-24 w-24 border-4 border-white">
              <AvatarImage src={user?.profileImage} />
              <AvatarFallback className="text-2xl">{user?.name?.[0]}</AvatarFallback>
            </Avatar>
          </div>
        </div>

        {/* Content */}
        <div className="pt-14 pb-6 px-6 text-center">
          {/* Name */}
          <h2 className="text-2xl font-bold mb-1">{user?.name}</h2>
          
          {/* Position Badge */}
          {user?.position && (
            <Badge 
              className="mb-2"
              style={{ backgroundColor: themeColor, color: 'white' }}
            >
              {user.position === 'goalkeeper' ? 'Kaleci' :
               user.position === 'defender' ? 'Defans' :
               user.position === 'midfielder' ? 'Orta Saha' : 'Forvet'}
            </Badge>
          )}

          {/* Club Info */}
          {user?.clubName && (
            <div className="flex items-center justify-center gap-2 mb-3">
              <Shield className="h-4 w-4" style={{ color: themeColor }} />
              <span className="text-sm font-medium">{user.clubName}</span>
            </div>
          )}

          {/* Connection Count */}
          {user?.connectionCount !== undefined && (
            <div className="flex items-center justify-center gap-1 text-sm text-gray-600 mb-3">
              <Users className="h-4 w-4" />
              <span>{user.connectionCount || 0} Bağlantı</span>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: themeColor }}>
                {user?.record?.totalPoints || 0}
              </p>
              <p className="text-xs text-gray-600">Puan</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: themeColor }}>
                {user?.record?.matchesPlayed || 0}
              </p>
              <p className="text-xs text-gray-600">Maç</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: themeColor }}>
                {user?.record?.goals || 0}
              </p>
              <p className="text-xs text-gray-600">Gol</p>
            </div>
          </div>

          {/* Titles Owned */}
          {user?.titles && user.titles.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-500 mb-2">ÜNVANLAR</p>
              <div className="flex flex-wrap justify-center gap-1">
                {user.titles.slice(0, 5).map((titleId) => {
                  const title = TITLE_MAP[titleId];
                  if (!title) return null;
                  return (
                    <Badge 
                      key={titleId}
                      variant="outline" 
                      className={`text-xs ${user.activeTitle === titleId ? 'border-purple-500 bg-purple-50' : ''}`}
                    >
                      {title.icon} {title.name}
                    </Badge>
                  );
                })}
                {user.titles.length > 5 && (
                  <Badge variant="outline" className="text-xs">
                    +{user.titles.length - 5}
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Physical Stats */}
          {(user?.height || user?.weight || user?.age) && (
            <div className="flex justify-center gap-4 text-sm text-gray-600 mb-4">
              {user?.height && <span>{user.height} cm</span>}
              {user?.weight && <span>{user.weight} kg</span>}
              {user?.age && <span>{user.age} yaş</span>}
            </div>
          )}

          {/* Location */}
          {(user?.city || user?.district) && (
            <div className="text-sm text-gray-600 mb-4">
              📍 {user.district && `${user.district}, `}{user.city}
            </div>
          )}

          {/* Preferred Foot & Play Style */}
          <div className="flex justify-center gap-2 mb-4 flex-wrap">
            {user?.preferredFoot && (
              <Badge variant="outline">
                {user.preferredFoot === 'left' ? '⚽ Sol Ayak' :
                 user.preferredFoot === 'right' ? '⚽ Sağ Ayak' : '⚽ İki Ayak'}
              </Badge>
            )}
            {user?.playStyle && (
              <Badge variant="outline">
                {user.playStyle === 'aggressive' ? '🔥 Agresif' :
                 user.playStyle === 'passer' ? '🎯 Pasçı' :
                 user.playStyle === 'technical' ? '⭐ Teknik' :
                 user.playStyle === 'sprinter' ? '⚡ Hızlı' : '⚖️ Dengeli'}
              </Badge>
            )}
          </div>

          {/* Experience */}
          {user?.experience && (
            <div className="text-sm text-gray-600 mb-4">
              <strong>Deneyim:</strong> {
                user.experience === 'Beginner' ? 'Başlangıç' :
                user.experience === 'Amateur' ? 'Amatör' :
                user.experience === 'Semi-Pro' ? 'Yarı Profesyonel' : 'Profesyonel'
              }
            </div>
          )}

          {/* Bio */}
          {user?.bio && (
            <p className="text-sm text-gray-700 italic border-t pt-4">
              "{user.bio}"
            </p>
          )}

          {/* Technical Skills Bar Chart */}
          {user?.technicalCard && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs font-semibold text-gray-600 mb-3">TEKNİK YETENEKLER</p>
              <div className="space-y-2">
                {[
                  { label: 'Pas', value: user.technicalCard.passing },
                  { label: 'Şut', value: user.technicalCard.shooting },
                  { label: 'Kontrol', value: user.technicalCard.ballControl },
                  { label: 'Hız', value: user.technicalCard.speed }
                ].map((skill) => (
                  <div key={skill.label} className="flex items-center gap-2">
                    <span className="text-xs w-16 text-left">{skill.label}</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ 
                          width: `${skill.value}%`, 
                          backgroundColor: themeColor 
                        }}
                      />
                    </div>
                    <span className="text-xs w-8 text-right">{skill.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div 
          className="py-2 text-center text-xs font-semibold text-white"
          style={{ backgroundColor: themeColor }}
        >
          KRAMPON PLAYER CARD
        </div>
      </Card>
    </div>
  );
};

export default PlayerCard;
